import { useEffect, useState } from 'react';
import { useShow, useDelete } from '@refinedev/core';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import type { TfTrackRow } from '@verybigthings/trackframe-shared/schemas';
import { trackLabel } from '@verybigthings/trackframe-shared/tracks';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fmtIso } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AccessTab } from './access/AccessTab';
import { RolesTab } from './roles/RolesTab';
import { TrackSettingsPanel } from './settings/TrackSettingsPanel';

// Tab-name segment the App.tsx route allows after `show/:id/` (HUB-109 /
// TAZ-07). Unknown segments fall back to the overview.
export const TRACK_TABS = ['overview', 'access', 'roles', 'settings'] as const;
export type TrackTab = (typeof TRACK_TABS)[number];

function isTrackTab(value: string): value is TrackTab {
  return (TRACK_TABS as readonly string[]).includes(value);
}

export function tabFromPath(pathname: string, trackId: string | undefined): TrackTab {
  if (!trackId) return 'overview';
  const base = `/admin/tracks/show/${trackId}`;
  if (!pathname.startsWith(base)) return 'overview';
  const segment = pathname.slice(base.length).replace(/^\/+|\/+$/g, '');
  return isTrackTab(segment) ? segment : 'overview';
}

export function tabPath(trackId: string, tab: TrackTab): string {
  const base = `/admin/tracks/show/${trackId}`;
  return tab === 'overview' ? base : `${base}/${tab}`;
}

/** The four triggers — exported so the order is pinned by a static render. */
export function TrackTabsList() {
  return (
    <TabsList>
      <TabsTrigger value="overview">Overview</TabsTrigger>
      <TabsTrigger value="access">Access</TabsTrigger>
      <TabsTrigger value="roles">Roles</TabsTrigger>
      <TabsTrigger value="settings">Settings</TabsTrigger>
    </TabsList>
  );
}

function ConfigurationCard({ track }: { track: TfTrackRow }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Configuration</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-y-2 text-sm md:grid-cols-2">
        <div className="text-muted-foreground">ID</div>
        <div className="font-mono text-xs">{track.id}</div>
        <div className="text-muted-foreground">Name (slug)</div>
        <div className="font-mono text-xs">{track.name}</div>
        <div className="text-muted-foreground">API URL</div>
        <div className="font-mono text-xs break-all">{track.apiUrl}</div>
        <div className="text-muted-foreground">Frontend URL</div>
        <div className="font-mono text-xs break-all">
          {track.baseUrl ? (
            <a href={track.baseUrl} target="_blank" rel="noreferrer" className="underline">
              {track.baseUrl}
            </a>
          ) : (
            '—'
          )}
        </div>
        <div className="text-muted-foreground">Health-check URL</div>
        <div className="font-mono text-xs break-all">{track.healthCheckUrl ?? '—'}</div>
        <div className="text-muted-foreground">OpenAPI</div>
        <div>{track.openapiSpecJson ? 'present' : '—'}</div>
        <div className="text-muted-foreground">Created</div>
        <div>{fmtIso(track.createdAt)}</div>
        <div className="text-muted-foreground">Updated</div>
        <div>{fmtIso(track.updatedAt)}</div>
      </CardContent>
    </Card>
  );
}

export function TracksShow() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  // Resource + id passed explicitly: the component also mounts under
  // `show/:id/:tab`, which does not match the registered resource path
  // (same reason as users/Show.tsx).
  const { query } = useShow<TfTrackRow>({ resource: 'tracks', id: params.id });
  const track = query.data?.data;
  const { mutate: deleteTrack, mutation: deleteMutation } = useDelete();
  const deleting = deleteMutation.isPending;
  const [confirmDelete, setConfirmDelete] = useState(false);

  // URL <-> tab sync; a tab change pushes a history entry so Back toggles tabs.
  const [tab, setTab] = useState<TrackTab>(() => tabFromPath(location.pathname, params.id));
  useEffect(() => {
    setTab(tabFromPath(location.pathname, params.id));
  }, [location.pathname, params.id]);
  const handleTabChange = (next: string) => {
    const value = isTrackTab(next) ? next : 'overview';
    setTab(value);
    if (!params.id) return;
    const target = tabPath(params.id, value);
    if (location.pathname !== target) navigate(target, { replace: false });
  };

  if (query.isLoading) return <div>Loading…</div>;
  if (!track) return <div>Not found.</div>;

  const onDelete = () =>
    deleteTrack(
      { resource: 'tracks', id: track.id },
      {
        onSuccess: () => navigate('/admin/tracks'),
      },
    );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{trackLabel(track)}</h1>
          <p className="text-sm text-muted-foreground">{track.description ?? '—'}</p>
        </div>
        <div className="flex gap-2">
          <Link to={`/admin/tracks/edit/${track.id}`} className={cn(buttonVariants({ variant: 'outline' }))}>
            Edit
          </Link>
          <Button variant="destructive" onClick={() => setConfirmDelete(true)} disabled={deleting}>
            Delete
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={handleTabChange}>
        <TrackTabsList />
        <TabsContent value="overview">
          <ConfigurationCard track={track} />
        </TabsContent>
        <TabsContent value="access">
          <AccessTab trackId={track.id} trackName={trackLabel(track)} />
        </TabsContent>
        <TabsContent value="roles">
          <RolesTab trackId={track.id} />
        </TabsContent>
        <TabsContent value="settings">
          <TrackSettingsPanel trackId={track.id} />
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete track?"
        description={`"${track.name}" will be removed. Sessions referencing it will keep history but lose routing.`}
        confirmLabel="Delete"
        destructive
        busy={deleting}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={onDelete}
      />
    </div>
  );
}
